import { MutableRefObject, useRef, useState } from 'react';

import { GearbeltEvent } from '@gearbelt/global';
import { PhoneCheckContext } from '@site-mate/sitemate-global-shared/lib';

import { ApiService } from '@/services/api-v2';
import { signupOtpService } from '@/services/global-api/signup-otp.service';
import { SignUpSteps } from '@/stores/signup.store';

import { PhoneVerificationErrorReason, PhoneVerificationStatus } from './sign-up-phone-verification';
import { mapSendErrorReason, mapVerifyErrorReason, VALID_CAPTCHA_STATUS } from './signup-otp-utils';

interface UseSignupOtpFlowParams {
  getPhone: () => string;
  persistForm: () => void;
  onSubmit: () => Promise<void>;
  executeRecaptcha?: (action: string) => Promise<string>;
  trackEvent: (event: GearbeltEvent, properties?: Record<string, unknown>) => void;
  setSignUpStep: (step: SignUpSteps) => void;
  setLoading: (loading: boolean) => void;
  showErrorToast: (message: string) => void;
  useReCAPTCHAV2: boolean;
  reCAPTCHAV2Token: string | null;
  setUseReCAPTCHAV2: (value: boolean) => void;
  setReCAPTCHAV2Token: (token: string | null) => void;
  humanVerificationPurposeRef: MutableRefObject<'signup' | 'otp-send'>;
}

export function useSignupOtpFlow({
  getPhone,
  persistForm,
  onSubmit,
  executeRecaptcha,
  trackEvent,
  setSignUpStep,
  setLoading,
  showErrorToast,
  useReCAPTCHAV2,
  reCAPTCHAV2Token,
  setUseReCAPTCHAV2,
  setReCAPTCHAV2Token,
  humanVerificationPurposeRef,
}: UseSignupOtpFlowParams) {
  const otpTwoFactorIdRef = useRef<string | null>(null);
  const verificationTokenRef = useRef<string | null>(null);
  const [otpStatus, setOtpStatus] = useState<PhoneVerificationStatus>('entry');
  const [otpErrorReason, setOtpErrorReason] = useState<PhoneVerificationErrorReason | undefined>(undefined);
  const [otpCooldownSeconds, setOtpCooldownSeconds] = useState(0);
  // Preserved across the v2 detour so a resend keeps the chosen delivery method instead of reverting to SMS.
  const otpDeliveryMethodRef = useRef<'sms' | 'call'>('sms');
  const otpProfileMatchCountRef = useRef<number>(0);

  async function onMobileNumberNext() {
    persistForm();

    setLoading(true);
    try {
      const phone = getPhone();
      const { clash, matchCount } = await ApiService.CheckPhoneOnSignup({ phone, context: PhoneCheckContext.Signup });

      if (!clash) {
        await onSubmit();
        return;
      }

      otpProfileMatchCountRef.current = matchCount;
      await sendOtpAndRoute('sms', useReCAPTCHAV2, true);
    } catch (error) {
      showErrorToast('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  // Send the OTP then route: valid captcha → verify screen; low v3 → v2 challenge (then re-send); send error → verify screen.
  async function sendOtpAndRoute(deliveryMethod: 'sms' | 'call', useV2Fallback: boolean, trackSent = false) {
    otpDeliveryMethodRef.current = deliveryMethod;
    const phone = getPhone();

    let recaptchaToken = '';
    if (useV2Fallback) {
      recaptchaToken = reCAPTCHAV2Token ?? '';
    } else if (executeRecaptcha) {
      recaptchaToken = await executeRecaptcha('send_otp');
    }

    let sendResult: Awaited<ReturnType<typeof signupOtpService.send>>;
    try {
      sendResult = await signupOtpService.send(phone, recaptchaToken, deliveryMethod, useV2Fallback, PhoneCheckContext.Signup);
    } catch (error) {
      const { reason, cooldownSeconds } = mapSendErrorReason(error);
      setOtpErrorReason(reason);
      setOtpCooldownSeconds(cooldownSeconds);
      setOtpStatus('error');
      setSignUpStep(SignUpSteps.PhoneVerification);
      return;
    }

    if (sendResult.status !== VALID_CAPTCHA_STATUS || !sendResult.twoFactorId) {
      trackEvent(GearbeltEvent.SignUpHumanVerificationRequired, {});
      humanVerificationPurposeRef.current = 'otp-send';
      setUseReCAPTCHAV2(true);
      setSignUpStep(SignUpSteps.HumanVerification);
      return;
    }

    otpTwoFactorIdRef.current = sendResult.twoFactorId;
    // Only on the clash-triggered send, not on resend / request-a-call.
    if (trackSent) {
      trackEvent(GearbeltEvent.SignupOtpSent, { profileMatchCount: otpProfileMatchCountRef.current });
    }
    // Clear v2 fallback state so a consumed OTP token can't leak into the account-creation reCAPTCHA onSubmit mints.
    setUseReCAPTCHAV2(false);
    setReCAPTCHAV2Token(null);
    setOtpErrorReason(undefined);
    setOtpCooldownSeconds(0);
    setOtpStatus('entry');
    setSignUpStep(SignUpSteps.PhoneVerification);
  }

  async function onOtpVerify(code: string) {
    if (!otpTwoFactorIdRef.current) {
      setOtpErrorReason('expired');
      setOtpStatus('error');
      return;
    }

    setOtpErrorReason(undefined);
    setOtpStatus('submitting');
    try {
      const { verificationToken: token } = await signupOtpService.verify(otpTwoFactorIdRef.current, code);
      verificationTokenRef.current = token;
      trackEvent(GearbeltEvent.SignupOtpVerified);
    } catch (error) {
      const failureReason = mapVerifyErrorReason(error);
      trackEvent(GearbeltEvent.SignupOtpFailed, { failureReason });
      setOtpErrorReason(failureReason);
      setOtpStatus('error');
      return;
    }

    // onSubmit navigates away on success; on any return-or-throw it leaves us here, so always clear the spinner.
    try {
      await onSubmit();
    } finally {
      setOtpStatus('entry');
    }
  }

  async function onOtpResend() {
    await sendOtpAndRoute('sms', useReCAPTCHAV2);
  }

  async function onOtpRequestCall() {
    await sendOtpAndRoute('call', useReCAPTCHAV2);
  }

  function resetForNewNumber() {
    otpTwoFactorIdRef.current = null;
    // Drop the previous number's token so it can't be submitted against a different phone.
    verificationTokenRef.current = null;
    setOtpErrorReason(undefined);
    setOtpStatus('entry');
    setSignUpStep(SignUpSteps.MobileNumber);
  }

  return {
    otpStatus,
    otpErrorReason,
    otpCooldownSeconds,
    verificationTokenRef,
    otpDeliveryMethodRef,
    onMobileNumberNext,
    sendOtpAndRoute,
    onOtpVerify,
    onOtpResend,
    onOtpRequestCall,
    resetForNewNumber,
  };
}
